import * as React from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { SYNC_API, type SyncService } from '@chess/api-sync';
import { Landing } from './Landing';
import { readUsername } from './account';
import './landing.css';

// The landing page: what the app does, and one way in — a chess.com
// username. A visitor with no username saved is sent here from any
// route; once saved, the app opens (through the sync screen's gate: the
// account's first sync). It is never gated itself, so a user with an
// account can come back to change it: the form shows the saved username,
// and saving another announces `settings.chesscom.changed`, on which the
// store reloads for the new account. A save always starts the account over
// (`sync.reset()`: its games, scans and bookkeeping go) and leads into the
// first sync again (`/sync` with `force`), the same account or not.
export function setup(app: PiletApi) {
  const settings = app.getApi<Storage>(STORAGE_API).then((s) => s.scope('settings'));
  // The app's name and tagline come from the platform's `brand`, never hardcoded here.
  const brand = app.tryGetApi('brand');

  app.registerPage('/welcome', () => (
    <Landing
      settings={settings}
      brand={brand}
      onSaved={async (username) => {
        app.emit('settings.chesscom.changed', { username });
        await (await app.getApi<SyncService>(SYNC_API)).reset();
      }}
    />
  ));

  // The landing page takes the whole viewport: no rail, no bottom bar. The
  // frame reads this contribution and leaves them out on this route.
  app.registerExtension('shell.chromeless', () => null, { path: '/welcome' });

  // The gate: rendered by the frame on every screen, draws nothing, and
  // redirects to the landing page while no username is saved.
  const Gate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toLanding = (username: string) => {
      if (!username && location.pathname !== '/welcome') navigate('/welcome', { replace: true });
    };
    React.useEffect(() => {
      let cancelled = false;
      settings
        .then(readUsername)
        .then((username) => {
          if (!cancelled) toLanding(username);
        })
        .catch(() => undefined);
      return () => {
        cancelled = true;
      };
    }, [location.pathname]);
    // Clearing the username in settings brings the landing page back.
    React.useEffect(() => {
      const handler = ({ username }: { username: string }) => toLanding(username);
      app.on('settings.chesscom.changed', handler);
      return () => {
        app.off('settings.chesscom.changed', handler);
      };
    }, [location.pathname]);
    return null;
  };
  app.registerExtension('shell.gate', Gate);
}
